// src/pages/Campaigns/VolunteerManagement/OutreachDistribution.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, Grid, Card, CardContent, CardActions,
    TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Chip, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Alert, Snackbar, CircularProgress,
    InputAdornment, Tooltip, FormControl, InputLabel,
    Select, MenuItem 
} from '@mui/material';
import {
    ArrowBack, Email, Sms, Link as LinkIcon, Code, QrCode,
    Send, ContentCopy, Download, Share, People, OpenInNew
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import axiosInstance from '../../../../utils/axiosInstance';
import { CHATBOT_UI } from '../../../../config';

const OutreachDistribution = () => {
    const { eventId, chatbotId } = useParams();
    const navigate = useNavigate();
    
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    // Dialog states
    const [embedDialog, setEmbedDialog] = useState(false);
    const [qrDialog, setQrDialog] = useState(false);
    const [linkDialog, setLinkDialog] = useState(false);
    
    // Statistics
    const [stats, setStats] = useState({
        total_volunteers: 0,
        emails_sent: 0,
        sms_sent: 0,
        responses_received: 0
    });

    const [embedSettings, setEmbedSettings] = useState({
        type: 'iframe',
        width: '100%',
        height: '600px'
    });

    const fetchEventAndStats = useCallback(async () => {
        try {
            setLoading(true);
            
            // Fetch event details
            const eventRes = await axiosInstance.get('/api/events', { 
                params: { chatbot_id: chatbotId } 
            });
            const foundEvent = eventRes.data.data.find(e => e.id === parseInt(eventId));
            setEvent(foundEvent);
            
            // Fetch campaign statistics
            try {
                const statsRes = await axiosInstance.get('/api/outreach/stats', {
                    params: { event_id: eventId, chatbot_id: chatbotId }
                });
                if (statsRes.data.success) {
                    setStats(statsRes.data.data);
                }
            } catch (statsError) {
                console.log('Stats not available yet');
            }
            
        } catch (error) {
            console.error('Error fetching data:', error);
            showSnackbar('Error loading event data', 'error');
        } finally {
            setLoading(false);
        }
    }, [eventId, chatbotId]);

    useEffect(() => {
        fetchEventAndStats();
    }, [fetchEventAndStats]);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const getPublicLink = () => {
        if (!event) return 'Loading...';
        // This would be a public volunteer registration link for this event
        return `${window.location.origin}/volunteer/register/${chatbotId}?event=${eventId}`;
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        showSnackbar('Copied to clipboard!');
    };

    const generateEmbedCode = () => {
        const url = getPublicLink();
        switch (embedSettings.type) {
            case 'iframe':
                return `<iframe src="${url}" width="${embedSettings.width}" height="${embedSettings.height}" frameborder="0"></iframe>`;
            case 'popup':
                return `<script>function openVolunteerForm(){window.open('${url}','volunteer','width=600,height=700,scrollbars=yes');}</script><button onclick="openVolunteerForm()">Volunteer Now</button>`;
            case 'button':
                return `<a href="${url}" target="_blank" style="display:inline-block;padding:12px 24px;background:#1976d2;color:white;text-decoration:none;border-radius:4px;">Volunteer for ${event?.name || 'Event'}</a>`;
            default:
                return '';
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (!event) {
        return (
            <Box sx={{ p: 3, mt: 10 }}>
                <Alert severity="error">Event not found</Alert>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/events`)}
                    sx={{ mt: 2 }}
                >
                    Back to Events
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, mt: 10 }}>
            {/* Header */}
            <Box display="flex" alignItems="center" mb={3}>
                <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/events`)} sx={{ mr: 2 }}>
                    <ArrowBack />
                </IconButton>
                <Box flex={1}>
                    <Typography variant="h5" fontWeight="bold">
                        Create Outreach Campaign
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {event?.name}
                    </Typography>
                </Box>
                <Chip 
                    label={event?.status} 
                    color={event?.status === 'SCHEDULED' ? 'success' : 'default'}
                    sx={{ mr: 2 }}
                />
            </Box>

            {/* Event Summary Card */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">Event Date</Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {new Date(event.event_start_date).toLocaleDateString()} - {new Date(event.event_end_date).toLocaleDateString()}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">Location</Typography>
                        <Typography variant="body1" fontWeight={600}>{event.location}</Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">Volunteers Needed</Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {event.total_assigned_volunteers} / {event.total_required_volunteers}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">Responses</Typography>
                        <Typography variant="body1" fontWeight={600}>{stats.responses_received}</Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Quick Share Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Quick Share Link</Typography>
                <Box display="flex" alignItems="center" gap={2}>
                    <TextField
                        fullWidth
                        value={getPublicLink()}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title="Copy Link">
                                        <IconButton onClick={() => copyToClipboard(getPublicLink())}>
                                            <ContentCopy />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Open in New Tab">
                                        <IconButton onClick={() => window.open(getPublicLink(), '_blank')}>
                                            <OpenInNew />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<QrCode />}
                        onClick={() => setQrDialog(true)}
                    >
                        QR Code
                    </Button>
                </Box>
            </Paper>

            {/* Distribution Channels */}
            <Typography variant="h6" gutterBottom>Distribution Channels</Typography>
            <Grid container spacing={3} mb={3}>
                {/* Email */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <Email sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h6">Email</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Send volunteer invitations via email
                                    </Typography>
                                </Box>
                            </Box>
                            {stats.emails_sent > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    {stats.emails_sent} emails sent
                                </Typography>
                            )}
                        </CardContent>
                        <CardActions>
                            <Button 
                                startIcon={<Send />} 
                                onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/outreach/email/${eventId}`)}
                            >
                                Send Emails
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>

                {/* SMS */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <Sms sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h6">SMS</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Send volunteer invitations via text message
                                    </Typography>
                                </Box>
                            </Box>
                            {stats.sms_sent > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    {stats.sms_sent} SMS sent
                                </Typography>
                            )}
                        </CardContent>
                        <CardActions>
                            <Button 
                                startIcon={<Send />} 
                                onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/outreach/sms/${eventId}`)}
                            >
                                Send SMS
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>

                {/* Embed */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <Code sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h6">Embed</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Embed volunteer signup on your website
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                        <CardActions>
                            <Button 
                                startIcon={<Code />} 
                                onClick={() => setEmbedDialog(true)}
                            >
                                Get Embed Code
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>
            </Grid>

            {/* Campaign History */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Campaign History</Typography>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Campaign</TableCell>
                                <TableCell>Channel</TableCell>
                                <TableCell>Sent</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Responses</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {event.outreach_campaigns && event.outreach_campaigns.length > 0 ? (
                                event.outreach_campaigns.map((campaign, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>{campaign.campaign_name}</TableCell>
                                        <TableCell>
                                            {campaign.send_email && <Chip label="Email" size="small" sx={{ mr: 0.5 }} />}
                                            {campaign.send_sms && <Chip label="SMS" size="small" />}
                                        </TableCell>
                                        <TableCell>{campaign.total_sent}</TableCell>
                                        <TableCell>
                                            {campaign.sent_at ? new Date(campaign.sent_at).toLocaleDateString() : '-'}
                                        </TableCell>
                                        <TableCell>{campaign.total_responded}</TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <Typography color="text.secondary">
                                            No campaigns sent yet. Use the channels above to reach volunteers.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Embed Dialog */}
            <Dialog open={embedDialog} onClose={() => setEmbedDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Embed Volunteer Registration Form</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Embed Type</InputLabel>
                        <Select
                            value={embedSettings.type}
                            label="Embed Type"
                            onChange={(e) => setEmbedSettings({ ...embedSettings, type: e.target.value })}
                        >
                            <MenuItem value="iframe">iFrame (Inline)</MenuItem>
                            <MenuItem value="popup">Popup Window</MenuItem>
                            <MenuItem value="button">Button Link</MenuItem>
                        </Select>
                    </FormControl>
                    {embedSettings.type === 'iframe' && (
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Width"
                                    value={embedSettings.width}
                                    onChange={(e) => setEmbedSettings({ ...embedSettings, width: e.target.value })}
                                    margin="normal"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Height"
                                    value={embedSettings.height}
                                    onChange={(e) => setEmbedSettings({ ...embedSettings, height: e.target.value })}
                                    margin="normal"
                                />
                            </Grid>
                        </Grid>
                    )}
                    <TextField
                        fullWidth
                        label="Embed Code"
                        value={generateEmbedCode()}
                        multiline
                        rows={6}
                        margin="normal"
                        InputProps={{ readOnly: true }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmbedDialog(false)}>Close</Button>
                    <Button 
                        variant="contained" 
                        startIcon={<ContentCopy />} 
                        onClick={() => {
                            copyToClipboard(generateEmbedCode());
                            setEmbedDialog(false);
                        }}
                    >
                        Copy Code
                    </Button>
                </DialogActions>
            </Dialog>

            {/* QR Code Dialog */}
            <Dialog open={qrDialog} onClose={() => setQrDialog(false)}>
                <DialogTitle>QR Code</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" alignItems="center" p={2}>
                        <QRCodeSVG 
                            id="qr-code-outreach"
                            value={getPublicLink()} 
                            size={256}
                            level="H"
                            includeMargin
                        />
                        <Typography variant="body2" color="text.secondary" mt={2}>
                            Scan to volunteer for {event?.name}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setQrDialog(false)}>Close</Button>
                    <Button 
                        variant="contained" 
                        startIcon={<Download />}
                        onClick={() => {
                            const svg = document.querySelector('#qr-code-outreach');
                            if (svg) {
                                const svgData = new XMLSerializer().serializeToString(svg);
                                const canvas = document.createElement('canvas');
                                const ctx = canvas.getContext('2d');
                                const img = new Image();
                                img.onload = () => {
                                    canvas.width = img.width;
                                    canvas.height = img.height;
                                    ctx.drawImage(img, 0, 0);
                                    const pngFile = canvas.toDataURL('image/png');
                                    const downloadLink = document.createElement('a');
                                    downloadLink.download = `volunteer-qr-${event?.name}.png`;
                                    downloadLink.href = pngFile;
                                    downloadLink.click();
                                };
                                img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
                            }
                        }}
                    >
                        Download PNG
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert 
                    severity={snackbar.severity} 
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default OutreachDistribution;